import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@eriko/ui/src/components/ui/card';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Label } from '@eriko/ui/src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { apiClient } from '../../../lib/api';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import { useAccountingStatus } from '../../../hooks/useAccounting';
import { useAuthStore } from '../../../stores/authStore';
import { adjustmentAccountEmptyHint } from './paymentSettingsState';

interface ExpenseAccount {
  id: string;
  externalId: string;
  provider: string;
  name: string;
  accountType: string;
}

/**
 * Surfaces the OrganizationSettings.adjustmentAccountId picker. When set,
 * the payment writer auto-posts a paired adjustment JE (QBO) / ManualJournal
 * (Xero) for variance on Payment/BillPayment writes. Until this is set,
 * variance still surfaces on the chat artifact's warning strip but no JE
 * gets posted — your bank reconciliation will show a gap equal to the
 * variance amount.
 */
export function PaymentSettingsCard() {
  const queryClient = useQueryClient();
  const { data: orgSettings } = useOrgSettings();
  const role = useAuthStore((s) => s.role);
  // The PATCH route is gated to OWNER/ADMIN server-side (403 otherwise); mirror
  // that on the client so members see a read-only control instead of a button
  // that fails on click.
  const canEdit = role === 'OWNER' || role === 'ADMIN';

  // The chart of accounts the picker reads is populated by the sync, so an empty
  // list usually just means the sync hasn't finished — tailor the empty-state copy
  // accordingly (don't tell a connected, mid-sync org to "connect and run a sync").
  const { data: accountingStatus } = useAccountingStatus();
  const syncInProgress = !!accountingStatus?.isSyncing || !!accountingStatus?.paused;

  // Initial value from current org settings; null/undefined → "(none)"
  const currentAdjustmentAccountId = orgSettings?.settings?.adjustmentAccountId ?? null;

  const [selected, setSelected] = useState<string | null>(currentAdjustmentAccountId);

  // Sync local selection when orgSettings finishes loading (or after invalidation).
  // Without this, useState's initializer captures null on first render before
  // useOrgSettings resolves, and the dirty-flag stays wrong until user touches.
  useEffect(() => {
    setSelected(currentAdjustmentAccountId);
  }, [currentAdjustmentAccountId]);

  const { data: accounts = [], isLoading } = useQuery<ExpenseAccount[]>({
    queryKey: ['orgPaymentAccounts'],
    queryFn: async () => {
      // apiClient's envelope interceptor already unwraps { success, data } →
      // res.data IS the ExpenseAccount[]. (Do NOT re-read .success/.data here —
      // that double-unwrap throws on success and shows "No expense accounts found".)
      // apiClient.baseURL already ends in /api — pass the path WITHOUT /api or it
      // doubles to /api/api/... and 404s (the synced expense accounts then look
      // missing). Matches the ENDPOINTS convention used elsewhere.
      const res = await apiClient.get<ExpenseAccount[]>('/org/settings/payments/accounts');
      return res.data;
    },
  });

  const save = useMutation({
    mutationFn: async (adjustmentAccountId: string | null) => {
      // Interceptor unwraps the envelope (and rejects on success:false), so
      // res.data is the payload directly — same pattern as the query above.
      const res = await apiClient.patch<unknown>(
        '/org/settings/payments',
        { adjustmentAccountId },
      );
      return res.data;
    },
    onSuccess: () => {
      // useOrgSettings uses ['org', 'settings'] — not ['orgSettings'] —
      // so invalidating the wrong key leaves the form showing stale state.
      queryClient.invalidateQueries({ queryKey: ['org', 'settings'] });
    },
  });

  const dirty = selected !== currentAdjustmentAccountId;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment adjustments</CardTitle>
        <CardDescription>
          When Eriko records a payment with variance (e.g. you paid $98 against a $100 bill because of a $2 fee), it can post
          a paired journal entry to your books — but it needs to know which expense account to charge. Pick one below.
          Leave unset and variance will show as a warning on the chat card without posting a journal entry, leaving a gap
          on your bank reconciliation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="adjustment-account">Adjustment account</Label>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">Loading your chart of accounts…</div>
          ) : accounts.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              {adjustmentAccountEmptyHint({
                isSyncing: syncInProgress,
                hasConfiguredAccount: !!currentAdjustmentAccountId,
              })}
            </div>
          ) : (
            <Select
              value={selected ?? '__none__'}
              onValueChange={(v) => setSelected(v === '__none__' ? null : v)}
              disabled={!canEdit}
            >
              <SelectTrigger id="adjustment-account">
                <SelectValue placeholder="Pick an expense account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">(none — surface variance as warning only)</SelectItem>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} ({a.accountType})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {!canEdit
              ? 'Only an owner or admin can change this.'
              : currentAdjustmentAccountId
                ? 'Currently set — adjustment JEs will post automatically.'
                : 'Not set — variance shows on chat artifacts but no JE posts.'}
          </div>
          <Button
            onClick={() => save.mutate(selected)}
            disabled={!canEdit || !dirty || save.isPending}
            size="sm"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>

        {save.isError && (
          <div className="text-sm text-destructive">
            {save.error instanceof Error ? save.error.message : 'Save failed'}
          </div>
        )}
        {save.isSuccess && !dirty && (
          <div className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</div>
        )}
      </CardContent>
    </Card>
  );
}
